// Shared phone-number formatting utilities.
//
// Numbers are stored throughout the app as a raw E.164-style string with the
// country code embedded (e.g. "+6512345678", no spaces). These helpers derive
// a naturally-spaced display form per country (e.g. "+65 1234 5678") without
// ever mutating the stored value — format for display only.
//
// Originally lived inline in PhoneInput.jsx; extracted here so any page can
// reuse the formatting without importing the input component. PhoneInput.jsx
// re-exports these same names for backwards compatibility.

// Country codes and formatting rules
export const COUNTRIES = [
  { code: 'SG', name: 'Singapore', dialCode: '+65', format: [4, 4], placeholder: '8123 4567' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', format: [3, 3, 4], placeholder: '123 456 7890' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', format: [3, 4, 4], placeholder: '812 3456 7890' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', format: [3, 3, 4], placeholder: '812 345 6789' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', format: [3, 3, 4], placeholder: '912 345 6789' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', format: [3, 3, 4], placeholder: '912 345 6789' },
  { code: 'CN', name: 'China', dialCode: '+86', format: [3, 4, 4], placeholder: '138 1234 5678' },
  { code: 'IN', name: 'India', dialCode: '+91', format: [5, 5], placeholder: '98765 43210' },
  { code: 'US', name: 'United States', dialCode: '+1', format: [3, 3, 4], placeholder: '(555) 123-4567' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', format: [4, 3, 4], placeholder: '7123 456789' },
  { code: 'AU', name: 'Australia', dialCode: '+61', format: [4, 3, 3], placeholder: '4123 456 789' },
];

export function normalizeE164(phone = '') {
  const digits = String(phone).replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

export function getCountryByDialCodeOrPhone(phone = '') {
  const raw = String(phone).trim();
  if (!raw) return null;

  // Prefer direct dialCode match on the string (e.g. "+65...")
  const byPrefix = COUNTRIES.find((c) => raw.startsWith(c.dialCode));
  if (byPrefix) return byPrefix;

  // Fallback: digits-only match
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  return (
    COUNTRIES.find((c) => digits.startsWith(c.dialCode.replace('+', ''))) || null
  );
}

export function getExpectedNationalNumberLength(phone = '') {
  const country = getCountryByDialCodeOrPhone(phone);
  if (!country) return null;
  return country.format.reduce((sum, n) => sum + n, 0);
}

function formatNationalDigits(digits, format) {
  if (!digits) return '';
  let formatted = '';
  let digitIndex = 0;
  for (let i = 0; i < format.length && digitIndex < digits.length; i++) {
    if (i > 0) formatted += ' ';
    const segmentLength = format[i];
    const segment = digits.substring(digitIndex, digitIndex + segmentLength);
    formatted += segment;
    digitIndex += segmentLength;
  }
  if (digitIndex < digits.length) {
    formatted += ' ' + digits.substring(digitIndex);
  }
  return formatted.trim();
}

export function formatE164ForDisplay(phone = '') {
  const raw = String(phone).trim();
  if (!raw) return '';

  const country = getCountryByDialCodeOrPhone(raw);
  if (!country) {
    // Best effort: show normalized digits
    return normalizeE164(raw) || raw;
  }

  const normalized = normalizeE164(raw);
  const dialDigits = country.dialCode.replace('+', '');
  const allDigits = normalized.replace(/\D/g, '');
  const nationalDigits = allDigits.startsWith(dialDigits)
    ? allDigits.slice(dialDigits.length)
    : allDigits;

  const nationalFormatted = formatNationalDigits(nationalDigits, country.format);
  return nationalFormatted ? `${country.dialCode} ${nationalFormatted}` : country.dialCode;
}
